import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { analyticsService } from '../services/analyticsService';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import {
  Eye,
  MousePointerClick,
  TrendingUp,
  Users,
  ArrowUpRight,
  Mail,
  Phone,
  Share2,
  Linkedin,
  Globe,
  MessageCircle,
  Download
} from 'lucide-react';

const COLORS = ['#3B9EFF', '#22d3ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const Analytics = () => {
  const [analytics, setAnalytics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const data = await analyticsService.getMyCardsAnalytics();
      // Ensure data is always an array
      setAnalytics(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Erreur lors du chargement des analytics');
      setAnalytics([]); // Reset to empty array on error
    } finally {
      setLoading(false);
    }
  };

  // Ensure analytics is always an array for calculations
  const analyticsArray = Array.isArray(analytics) ? analytics : [];

  // Calculate totals
  const totalViews = analyticsArray.reduce((sum, a) => sum + (a.views_count || 0), 0);
  const totalClicks = analyticsArray.reduce((sum, a) => sum + (a.clicks_count || 0), 0);
  const engagementRate = totalViews > 0 ? Math.round((totalClicks / totalViews) * 100) : 0;

  // Prepare chart data
  const barChartData = analyticsArray.map(a => ({
    name: (a.full_name || '').split(' ')[0], // First name only for chart
    fullName: a.full_name || '',
    vues: a.views_count || 0,
    clics: a.clicks_count || 0
  }));

  // Aggregate clicks by type across all cards
  const clicksByType = analyticsArray.reduce((acc, a) => {
    Object.entries(a.clicks_by_type || {}).forEach(([type, count]) => {
      acc[type] = (acc[type] || 0) + count;
    });
    return acc;
  }, {});

  const pieChartData = Object.entries(clicksByType).map(([name, value]) => ({
    name: getClickTypeLabel(name),
    value
  }));

  function getClickTypeLabel(type) {
    const labels = {
      email: 'Email',
      phone: 'Téléphone',
      mobile: 'Mobile',
      whatsapp: 'WhatsApp',
      linkedin: 'LinkedIn',
      website: 'Site web',
      share: 'Partage',
      vcard: 'vCard',
      google_review: 'Google Review'
    };
    return labels[type] || type;
  }

  function getClickTypeIcon(type) {
    const icons = {
      email: Mail,
      phone: Phone,
      mobile: Phone,
      whatsapp: MessageCircle,
      linkedin: Linkedin,
      website: Globe,
      share: Share2,
      vcard: Download,
      google_review: TrendingUp
    };
    return icons[type] || MousePointerClick;
  }

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a1a] border border-gray-700 rounded-lg p-3 shadow-xl">
          <p className="text-white font-medium mb-2">{payload[0]?.payload?.fullName || label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-[#2B4A6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement des analytics...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-white text-3xl font-bold mb-2">Analytics</h1>
          <p className="text-gray-400">Suivez les performances de vos cartes de visite</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-red-500">{error}</p>
          </div>
        )}

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          {/* Total Views */}
          <div className="bg-gradient-to-br from-[#3B9EFF]/10 to-cyan-500/5 border border-[#3B9EFF]/30 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-[#3B9EFF]/20 rounded-xl flex items-center justify-center">
                <Eye size={24} className="text-[#3B9EFF]" />
              </div>
              <span className="text-green-400 text-sm flex items-center">
                <ArrowUpRight size={16} />
                Actif
              </span>
            </div>
            <p className="text-4xl font-bold text-white mb-1">{totalViews}</p>
            <p className="text-gray-400 text-sm">Vues totales</p>
          </div>

          {/* Total Clicks */}
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/5 border border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                <MousePointerClick size={24} className="text-green-500" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white mb-1">{totalClicks}</p>
            <p className="text-gray-400 text-sm">Clics totaux</p>
          </div>

          {/* Engagement Rate */}
          <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/5 border border-purple-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                <TrendingUp size={24} className="text-purple-500" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white mb-1">{engagementRate}%</p>
            <p className="text-gray-400 text-sm">Taux d'engagement</p>
          </div>

          {/* Total Cards */}
          <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                <Users size={24} className="text-orange-500" />
              </div>
            </div>
            <p className="text-4xl font-bold text-white mb-1">{analyticsArray.length}</p>
            <p className="text-gray-400 text-sm">Cartes actives</p>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Bar Chart - Performance by Card */}
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
            <h2 className="text-white text-xl font-semibold mb-6">Performance par carte</h2>
            {barChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="name" stroke="#9ca3af" fontSize={12} />
                  <YAxis stroke="#9ca3af" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="vues" name="Vues" fill="#3B9EFF" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="clics" name="Clics" fill="#22d3ee" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                Aucune donnée disponible
              </div>
            )}
          </div>

          {/* Pie Chart - Clicks by Type */}
          <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
            <h2 className="text-white text-xl font-semibold mb-6">Types d'interactions</h2>
            {pieChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[300px] flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <MousePointerClick size={48} className="mx-auto mb-4 opacity-50" />
                  <p>Aucune interaction enregistrée</p>
                  <p className="text-sm mt-2">Les clics sur email, téléphone, etc. apparaîtront ici</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Detailed Cards Table */}
        <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
          <h2 className="text-white text-xl font-semibold mb-6">Détails par carte</h2>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-800">
                  <th className="text-left text-gray-400 font-medium py-3 px-4">Conseiller</th>
                  <th className="text-left text-gray-400 font-medium py-3 px-4">Poste</th>
                  <th className="text-center text-gray-400 font-medium py-3 px-4">Vues</th>
                  <th className="text-center text-gray-400 font-medium py-3 px-4">Clics</th>
                  <th className="text-center text-gray-400 font-medium py-3 px-4">Engagement</th>
                  <th className="text-right text-gray-400 font-medium py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {analyticsArray.map((card) => {
                  const cardEngagement = card.views_count > 0
                    ? Math.round((card.clicks_count / card.views_count) * 100)
                    : 0;
                  
                  return (
                    <tr key={card.card_id} className="border-b border-gray-800/50 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-4">
                        <span className="text-white font-medium">{card.full_name}</span>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-gray-400">{card.job_title}</span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center space-x-1 text-[#3B9EFF]">
                          <Eye size={16} />
                          <span>{card.views_count}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className="inline-flex items-center space-x-1 text-green-500">
                          <MousePointerClick size={16} />
                          <span>{card.clicks_count}</span>
                        </span>
                      </td>
                      <td className="py-4 px-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          cardEngagement >= 10 
                            ? 'bg-green-500/20 text-green-400' 
                            : cardEngagement > 0 
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {cardEngagement}%
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <Link
                          to={`/cards/${card.card_id}`}
                          className="text-[#3B9EFF] hover:text-[#2B8EEF] text-sm font-medium"
                        >
                          Voir détails
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          {analyticsArray.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Users size={48} className="mx-auto mb-4 opacity-50" />
              <p>Aucune carte créée</p>
              <Link to="/cards/new" className="text-[#3B9EFF] hover:underline mt-2 inline-block">
                Créer ma première carte
              </Link>
            </div>
          )}
        </div>

        {/* Click Types Breakdown */}
        {Object.keys(clicksByType).length > 0 && (
          <div className="mt-8 bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
            <h2 className="text-white text-xl font-semibold mb-6">Détail des interactions</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(clicksByType)
                .sort((a, b) => b[1] - a[1])
                .map(([type, count], index) => {
                  const Icon = getClickTypeIcon(type);
                  return (
                    <div
                      key={type}
                      className="bg-white/5 border border-gray-800 rounded-xl p-4 text-center"
                    >
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-3"
                        style={{ backgroundColor: `${COLORS[index % COLORS.length]}20` }}
                      >
                        <Icon size={20} style={{ color: COLORS[index % COLORS.length] }} />
                      </div>
                      <p className="text-2xl font-bold text-white mb-1">{count}</p>
                      <p className="text-gray-400 text-sm">{getClickTypeLabel(type)}</p>
                    </div>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Analytics;
